// Where each button sits on screen, relative to its channel number.
//
// ── The hardware ───────────────────────────────────────────────────────────
// A Smartify panel is TWO ROWS TALL and as many columns wide as it needs, and
// the channels run DOWN each column:
//
//     4-touch        6-gang            8-gang
//     1   3          1   3   5         1   3   5   7
//     2   4          2   4   6         2   4   6   8
//
// So channel i sits at row (i % 2), column floor(i / 2).
//
// ── Why this needs saying at all ───────────────────────────────────────────
// A CSS grid and a wrapping flex row both fill ROW BY ROW. Iterating channels
// in order therefore produces the TRANSPOSE of the panel — 1 2 / 3 4 — and
// someone standing at the switch has to mentally rotate the app to work out
// which button is which. On a 4-gang that means the top-right button turns on
// the wrong light.
//
// ── Two shells, two different amounts of room ──────────────────────────────
// DESKTOP can be two rows tall and grow sideways, so it reproduces the panel
// exactly — see PANEL_ROWS and the grid it drives.
//
// MOBILE cannot: three or more columns of labelled buttons do not fit a phone,
// so it renders two columns and wraps. That is an APPROXIMATION, and the
// approximation chosen keeps each physical column together as one row on
// screen, which is the next best thing to the real arrangement:
//
//     6-gang on a phone      1   2      <- panel column 1
//                            3   4      <- panel column 2
//                            5   6      <- panel column 3
//
// The 4-gang is the one case where two columns IS the panel, so there it is
// reproduced exactly rather than approximated.

// Panels are always two rows tall.
export const PANEL_ROWS = 2;

// Display position -> channel index, for a two-column grid showing a 4-gang.
export const FOUR_TOUCH_ORDER = [0, 2, 1, 3];

/** How many columns the real panel has for `count` channels. */
export function panelColumns(count) {
  const n = Number(count) || 0;
  return Math.ceil(n / PANEL_ROWS);
}

/**
 * Whether a renderer should lay this out as the panel itself — two rows deep,
 * filled downwards — rather than as a wrapped list.
 *
 * Below four channels there is nothing to transpose: one button is one button,
 * and a 2-gang is a pair either way. Forcing those into a two-row column would
 * stack a 2-gang vertically for no gain and change a layout that is not wrong.
 */
export const usesPanelGrid = (count) => (Number(count) || 0) >= 4;

/**
 * The order to render `count` channels in, for a container that fills ROW BY
 * ROW with TWO columns — i.e. the mobile screen.
 *
 * ⚠️ NOT for the desktop card. That one sets `gridAutoFlow: 'column'` with two
 * rows, which fills downwards on its own, so it renders channels in natural
 * order and must NOT also permute them — doing both would transpose it back.
 */
export function displayOrder(count) {
  const n = Number(count) || 0;
  // Two columns and two rows: the phone can show the panel exactly.
  if (n === 4) return FOUR_TOUCH_ORDER;
  // Anything else: natural order, which puts each panel column on its own row.
  return Array.from({ length: n }, (_, i) => i);
}

/**
 * Grid styles for a container that can be as wide as it needs — the desktop
 * card. Two rows, flowing down each column, so natural iteration order comes
 * out as the panel.
 */
export function panelGridSx(count) {
  const n = Number(count) || 0;
  if (!usesPanelGrid(n)) {
    return { display: 'grid', gridTemplateColumns: n > 1 ? '1fr 1fr' : '1fr', gap: 1 };
  }
  return {
    display: 'grid',
    gridTemplateRows: `repeat(${PANEL_ROWS}, 1fr)`,
    gridAutoFlow: 'column',
    // Equal columns that may get narrow on a wide panel — the button labels
    // already ellipsize, and matching the physical arrangement is worth more
    // here than a few extra characters of name.
    gridAutoColumns: 'minmax(0, 1fr)',
    gap: 1,
  };
}
